'use client'

import { cn } from '@/utils/styles/cn'
import { forwardRef, useCallback, useEffect, useRef } from 'react'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    onValueChange?: (value: string) => void
    sizeType?: 'sm' | 'md' | 'lg'
    onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void
    borderRadius?: number
    autoHighlight?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            type,
            sizeType = 'sm',
            onValueChange,
            onChange: propsOnChange,
            onEnter,
            borderRadius,
            onKeyDown,
            autoHighlight,
            ...props
        },
        ref
    ) => {
        const internalRef = useRef<HTMLInputElement | null>(null)

        const refCallback = useCallback(
            (node: HTMLInputElement | null) => {
                internalRef.current = node

                if (typeof ref === 'function') {
                    ref(node)
                } else if (typeof ref === 'object' && ref) {
                    ref.current = node
                }
            },
            [ref]
        )

        const hasHighlightedRef = useRef(false)
        useEffect(() => {
            if (!autoHighlight || hasHighlightedRef.current) return

            if (internalRef.current) {
                // Highlight the input value of the input
                hasHighlightedRef.current = true

                internalRef.current.select()
            }
        })

        /**
         * Helper function to handle input value change
         */
        const onChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                onValueChange?.(e.target.value)
                propsOnChange?.(e)
            },
            [onValueChange, propsOnChange]
        )

        return (
            <input
                onWheel={(e) => e.currentTarget.blur()}
                type={type}
                className={cn(
                    `bg-white border border-black/10 placeholder:text-muted-foreground/70 dark:placeholder:text-muted-foreground/30
                    flex w-full px-3 outline-hidden transition-all h-8
                    file:border-0 file:bg-transparent file:text-sm file:font-medium focus-within:outline-hidden
                    focus-within:outline-offset-0 focus-visible:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50`,
                    sizeType === 'sm' && 'h-8 text-[15px] ',
                    sizeType === 'md' && 'h-10 text-[16px] ',
                    sizeType === 'lg' && 'h-14 text-[16px] ',
                    className
                )}
                style={{ borderRadius: borderRadius ?? 6 }}
                onKeyDown={(e) => {
                    // Prevent shortcuts outside of the input of being triggered
                    if (e.key !== 'Enter') {
                        e.stopPropagation()
                    }

                    if (e.key === 'Enter' && onEnter) {
                        e.stopPropagation()
                        onEnter(e)
                    }

                    onKeyDown?.(e)
                }}
                ref={refCallback}
                onChange={onChange}
                {...props}
            />
        )
    }
)
Input.displayName = 'Input'

export { Input }
